import moment from "moment";
import { DateFormat } from "../../helpers/fixcodes";

// Tracking page: pure rules for the vehicles on job (POST Tracking rows), shared by the list, the map and the panel.

/** Date + time as the rest of the website shows it ("" when empty). */
export const formatTime = (value) => (value ? moment(value).format(DateFormat.MomentDisplayDateTimeAMPMFullFormat) : "");

/** How old the last GPS fix is, e.g. "2 minutes ago" ("" when empty). */
export const gpsAge = (value, now = new Date()) => (value ? moment(value).from(now) : "");

/** True when the vehicle has a usable GPS position (the single rule for list, markers and panel). */
export const hasLocation = (v) => Number(v?.vehicleLatitude) > 0 && Number(v?.vehicleLongitude) > 0;

/** A GPS position older than this is "stale": the car on the map may no longer be there. */
export const STALE_MINUTES = 15;

/** True when the vehicle has a position but its last GPS fix is older than STALE_MINUTES. */
export const isStale = (v, now = new Date()) => hasLocation(v) && Boolean(v.gpsDateTime) && moment(now).diff(moment(v.gpsDateTime), "minutes") >= STALE_MINUTES;

/** The trip's stage after pickup (null while the driver is on the way / on location). */
export function tripStage(v) {
  if (v.isCancelled) return { key: "cancelled", label: "Cancelled", icon: "fa-ban" };
  if (v.isCompleted) return { key: "completed", label: "Completed", icon: "fa-flag-checkered", time: formatTime(v.completedTime) };
  if (v.isDeparted) return { key: "active", label: "Departed", icon: "fa-route" };
  if (v.isBoarded) return { key: "active", label: "Boarded", icon: "fa-user-check", time: formatTime(v.boardedTime) };
  return null;
}

const point = (lat, lng) => (Number(lat) > 0 && Number(lng) > 0 ? { lat: Number(lat), lng: Number(lng) } : null);

/** Where the vehicle goes next: the pickup until the crew is on board, then the dropoff (null when unknown). */
export function nextStop(v) {
  const boarded = Boolean(v.isBoarded || v.isDeparted);
  const at = boarded ? point(v.dropoffLatitude, v.dropoffLongitude) : point(v.pickupLatitude, v.pickupLongitude);
  if (!at) return null;
  return { ...at, kind: boarded ? "dropoff" : "pickup", label: boarded ? v.dropoffLocation : v.pickupLocation };
}

/** The pickup and dropoff points the API sent (each null when missing). */
export const tripPoints = (v) => ({
  pickup: point(v.pickupLatitude, v.pickupLongitude),
  dropoff: point(v.dropoffLatitude, v.dropoffLongitude),
});

/** Car sprite turned to the vehicle's heading (5° steps). The API sends no host status, so the car is orange. */
export function markerIconUrl(bearing) {
  const deg = ((Math.round(Number(bearing) || 0) % 360) + 360) % 360;
  return `../assets/img/direction/Car/Car_orange_${deg - (deg % 5)}.png?v=1.6`;
}

export const NO_GPS = "nogps";
export const STALE_GPS = "stale";
const statusId = (label) => `s:${label}`;

/** Tiles of the loaded vehicles: one per trip status present (in the order met), then No GPS and Stale GPS. */
export function statusTiles(rows, now = new Date()) {
  const tiles = [];
  rows.forEach((v) => {
    const label = v.tripStatusDescription || "Unknown";
    const tile = tiles.find((t) => t.label === label);
    if (tile) tile.count += 1;
    else tiles.push({ id: statusId(label), label, count: 1 });
  });
  tiles.push({ id: NO_GPS, label: "No GPS", count: rows.filter((v) => !hasLocation(v)).length });
  tiles.push({ id: STALE_GPS, label: "Stale GPS", count: rows.filter((v) => isStale(v, now)).length });
  return tiles;
}

/** Rows of a tile ("" = all). */
export function filterRows(rows, tile, now = new Date()) {
  if (!tile) return rows;
  if (tile === NO_GPS) return rows.filter((v) => !hasLocation(v));
  if (tile === STALE_GPS) return rows.filter((v) => isStale(v, now));
  return rows.filter((v) => statusId(v.tripStatusDescription || "Unknown") === tile);
}

/** Lower-case text the list search matches against. */
export const searchText = (v) =>
  [v.vehicleNo, v.rideId, v.transactionId, v.activityDisplay, v.driverName, v.driverMoblieNo, v.paxName, v.employeeNo, v.tripStatusDescription]
    .filter((x) => x !== null && x !== undefined)
    .join(" ")
    .toLowerCase();

/** Same order as before: vehicle number, newest first. */
export const sortRows = (rows) => [...rows].sort((a, b) => String(b.vehicleNo ?? "").localeCompare(String(a.vehicleNo ?? "")));

/** Driver initials for the avatar ("" when no name). */
export const initials = (name) =>
  String(name ?? "")
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

/** Live Tracking needs a position and a running trip. */
export const canLiveTrack = (v) => Boolean(v && hasLocation(v) && v.transactionId && !v.isCompleted && !v.isCancelled);
